import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const EmailVerifyBanner = ({ testid = "verify-banner" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success(`Email inviata a ${user.email}`); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm" data-testid={testid}>
      <MailCheck className="h-5 w-5 shrink-0 text-amber-600" />
      <p className="flex-1"><b>Conferma la tua email</b> per fare il primo ordine: clicca il link che ti abbiamo mandato a {user.email}.</p>
      <button disabled={busy} onClick={resend} className="rounded-full bg-black px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid={`${testid}-resend`}>{busy ? "Invio..." : "Rimanda l'email"}</button>
    </div>
  );
};

export const B2BStatusBadge = () => {
  const { user } = useAuth();
  if (!user) return null;
  if (user.account_type === "b2b") return <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-600 px-3 py-1 text-xs font-bold text-white" data-testid="b2b-badge-active"><Building2 className="h-3.5 w-3.5" />Account B2B attivo{user.b2b?.company ? ` · ${user.b2b.company}` : ""}</span>;
  if (user.b2b_pending) return <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-cyan-700 bg-cyan-50 px-3 py-1 text-xs font-bold text-cyan-800" data-testid="b2b-badge-pending" title="Stiamo verificando i dati della tua azienda: ti avvisiamo via email"><Building2 className="h-3.5 w-3.5" />Richiesta B2B in verifica</span>;
  if (user.b2b_rejected) return <Link to="/assistenza#contatti" className="inline-flex items-center gap-1.5 rounded-full border border-rose-400 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700" data-testid="b2b-badge-rejected"><Building2 className="h-3.5 w-3.5" />Richiesta B2B non approvata · contattaci</Link>;
  return null;
};

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("loading");
  const [msg, setMsg] = useState("");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); setMsg("Il link non è completo."); return; }
    api.post(params.get("change") ? "/profile/email/confirm" : "/auth/verify-email", { token })
      .then(() => { setState("ok"); checkAuth?.(); })
      .catch((err) => { setState("error"); setMsg(formatApiError(err)); });
  }, [params, checkAuth]);
  return (
    <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="verify-email-page">
      {state === "loading" && <p className="text-stone-500">Verifica in corso...</p>}
      {state === "ok" && <>
        <MailCheck className="mx-auto h-12 w-12 text-teal-600" />
        <h1 className="mt-4 font-display text-4xl font-bold" data-testid="verify-email-ok">Email confermata!</h1>
        <p className="mt-2 text-stone-600">{params.get("change") ? "Da ora accedi con la nuova email." : "Ora puoi completare i tuoi ordini."}</p>
        <Link to="/catalogo" className="mt-6 inline-block rounded-full bg-amber-500 px-6 py-3 font-bold text-black">Vai al catalogo</Link>
      </>}
      {state === "error" && <>
        <h1 className="font-display text-3xl font-bold" data-testid="verify-email-error">Link non valido</h1>
        <p className="mt-2 text-stone-600">{msg}</p>
        <Link to="/account" className="mt-6 inline-block text-amber-600 underline">Vai al tuo account per richiederne uno nuovo</Link>
      </>}
    </div>
  );
}
